import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  collection,
  getDocs,
  addDoc,
  updateDoc,
  deleteDoc,
  doc,
} from "firebase/firestore";
import {
  getStorage,
  ref,
  uploadBytes,
  getDownloadURL,
} from "firebase/storage";
import { getAuth, onAuthStateChanged } from "firebase/auth";
import { db } from "../firebase";
import { FaExternalLinkAlt, FaEdit, FaTrash, FaAward } from "react-icons/fa";

interface Certification {
  id: string;
  title: string;
  institution: string;
  date: string;
  logo: string;
  link: string;
}

const CertificationsSection: React.FC = () => {
  const [certifications, setCertifications] = useState<Certification[]>([]);
  const [formData, setFormData] = useState<Certification>({
    id: "",
    title: "",
    institution: "",
    date: "",
    logo: "",
    link: "",
  });
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [loading, setLoading] = useState(false);

  const auth = getAuth();

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      setIsLoggedIn(!!user);
    });
    return unsubscribe;
  }, [auth]);

  const fetchCertifications = async () => {
    try {
      const querySnapshot = await getDocs(collection(db, "certifications"));
      const fetched: Certification[] = querySnapshot.docs.map((doc) => {
        const data = doc.data();
        return {
          id: doc.id,
          title: data.title,
          institution: data.institution,
          date: data.date,
          logo: data.logo,
          link: data.link,
        };
      });
      setCertifications(fetched);
    } catch (error) {
      console.error("Error fetching certifications:", error);
    }
  };

  useEffect(() => {
    fetchCertifications();
  }, []);

  const handleFormSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    const { id, ...data } = formData;
    try {
      let imageUrl = formData.logo;
      if (imageFile) {
        const storage = getStorage();
        const imageRef = ref(storage, `certifications/${imageFile.name}`);
        await uploadBytes(imageRef, imageFile);
        imageUrl = await getDownloadURL(imageRef);
      }
      if (id) {
        const docRef = doc(db, "certifications", id);
        await updateDoc(docRef, { ...data, logo: imageUrl });
      } else {
        await addDoc(collection(db, "certifications"), { ...data, logo: imageUrl });
      }
      fetchCertifications();
      resetForm();
    } catch (error) {
      console.error("Error saving certification:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id: string) => {
    try {
      const docRef = doc(db, "certifications", id);
      await deleteDoc(docRef);
      fetchCertifications();
    } catch (error) {
      console.error("Error deleting certification:", error);
    }
  };

  const resetForm = () => {
    setFormData({ id: "", title: "", institution: "", date: "", logo: "", link: "" });
    setImageFile(null);
    setIsEditing(false);
  };

  return (
    <section className="section-padding relative" id="certifications">
      {/* Background decoration */}
      <div className="absolute top-1/3 right-0 w-72 h-72 bg-primary-500/5 rounded-full blur-3xl translate-x-1/2" />

      <div className="max-w-6xl mx-auto relative">
        {/* Section Header */}
        <motion.div
          className="text-center mb-16"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
        >
          <motion.div
            className="section-badge"
            initial={{ opacity: 0, scale: 0.9 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ delay: 0.1 }}
          >
            <FaAward size={12} className="inline mr-1" />
            Achievements
          </motion.div>
          <h2 className="section-title">
            My <span className="gradient-text">Certifications</span>
          </h2>
          <p className="section-subtitle">
            Professional certifications and achievements
          </p>
        </motion.div>

        {/* Certifications Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {certifications.map((cert, index) => (
            <motion.div
              key={cert.id}
              className="glass-card overflow-hidden group relative"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.1, duration: 0.5 }}
              whileHover={{ y: -8, transition: { duration: 0.3 } }}
            >
              {/* Glow effect */}
              <div className="absolute -inset-0.5 bg-gradient-to-r from-primary-500 to-accent-500 rounded-2xl opacity-0 group-hover:opacity-15 blur-sm transition-opacity duration-500" />

              <div className="relative bg-white dark:bg-surface-800 rounded-2xl overflow-hidden">
                {/* Logo/Image */}
                <div className="relative h-44 overflow-hidden bg-gray-50 dark:bg-surface-900">
                  <img
                    src={cert.logo}
                    alt={cert.title}
                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />

                  {/* Shine sweep */}
                  <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500 overflow-hidden">
                    <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-1000" />
                  </div>

                  {/* Admin Actions */}
                  {isLoggedIn && (
                    <div className="absolute top-3 right-3 flex gap-2 opacity-0 group-hover:opacity-100 transition-all duration-300 translate-y-2 group-hover:translate-y-0">
                      <motion.button
                        onClick={() => {
                          setFormData(cert);
                          setIsEditing(true);
                        }}
                        className="p-2 bg-white/90 backdrop-blur-sm rounded-lg text-primary-600 hover:bg-white transition-colors shadow-sm"
                        whileHover={{ scale: 1.1 }}
                        whileTap={{ scale: 0.9 }}
                      >
                        <FaEdit size={12} />
                      </motion.button>
                      <motion.button
                        onClick={() => handleDelete(cert.id)}
                        className="p-2 bg-white/90 backdrop-blur-sm rounded-lg text-red-500 hover:bg-white transition-colors shadow-sm"
                        whileHover={{ scale: 1.1 }}
                        whileTap={{ scale: 0.9 }}
                      >
                        <FaTrash size={12} />
                      </motion.button>
                    </div>
                  )}
                </div>

                {/* Content */}
                <div className="p-6">
                  <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-1 group-hover:text-primary-600 dark:group-hover:text-primary-400 transition-colors duration-300">
                    {cert.title}
                  </h3>
                  <p className="text-sm text-gray-500 dark:text-gray-400 mb-1">
                    {cert.institution}
                  </p>
                  <p className="text-xs text-gray-400 dark:text-gray-500 mb-4">
                    {cert.date}
                  </p>
                  {cert.link && (
                    <a
                      href={cert.link}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 text-sm font-medium text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 transition-colors group/link"
                    >
                      View Certificate
                      <FaExternalLinkAlt size={10} className="group-hover/link:translate-x-0.5 group-hover/link:-translate-y-0.5 transition-transform" />
                    </a>
                  )}
                </div>
              </div>
            </motion.div>
          ))}
        </div>

        {certifications.length === 0 && (
          <motion.div
            className="text-center py-16 text-gray-400 dark:text-gray-600"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
          >
            <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-gray-100 dark:bg-surface-800 flex items-center justify-center">
              <FaAward size={24} className="text-gray-300 dark:text-gray-600" />
            </div>
            <p>No certifications yet.</p>
          </motion.div>
        )}

        {/* Add Button */}
        {isLoggedIn && (
          <div className="flex justify-center mt-10">
            <motion.button
              className="btn-primary"
              onClick={() => setIsEditing(true)}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              Add New Certification
            </motion.button>
          </div>
        )}
      </div>

      {/* Modal */}
      <AnimatePresence>
        {isEditing && (
          <motion.div
            className="modal-overlay"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={resetForm}
          >
            <motion.div
              className="modal-content"
              initial={{ scale: 0.9, opacity: 0, y: 20 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              exit={{ scale: 0.9, opacity: 0, y: 20 }}
              transition={{ type: "spring", stiffness: 300, damping: 25 }}
              onClick={(e) => e.stopPropagation()}
            >
              <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-6">
                {formData.id ? "Edit Certification" : "Add Certification"}
              </h3>
              {loading && (
                <div className="absolute inset-0 flex items-center justify-center bg-white/70 dark:bg-surface-800/70 backdrop-blur-sm z-10 rounded-2xl">
                  <div className="w-10 h-10 border-4 border-primary-200 border-t-primary-500 rounded-full animate-spin" />
                </div>
              )}
              <form onSubmit={handleFormSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">Title</label>
                  <input
                    type="text"
                    value={formData.title}
                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                    className="input-field"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">Institution</label>
                  <input
                    type="text"
                    value={formData.institution}
                    onChange={(e) => setFormData({ ...formData, institution: e.target.value })}
                    className="input-field"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">Date</label>
                  <input
                    type="date"
                    value={formData.date}
                    onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                    className="input-field"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">Logo/Image</label>
                  <input
                    type="file"
                    onChange={(e) => setImageFile(e.target.files?.[0] || null)}
                    className="input-field !py-2"
                  />
                  {formData.logo && !imageFile && (
                    <img
                      src={formData.logo}
                      alt="Certification"
                      className="mt-3 h-20 w-20 object-cover rounded-xl"
                    />
                  )}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">Certificate Link</label>
                  <input
                    type="url"
                    value={formData.link}
                    onChange={(e) => setFormData({ ...formData, link: e.target.value })}
                    className="input-field"
                  />
                </div>
                <div className="flex justify-end gap-3 pt-4">
                  <button
                    type="button"
                    className="btn-secondary !py-2.5 !px-5 !text-sm"
                    onClick={resetForm}
                    disabled={loading}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="btn-primary !py-2.5 !px-5 !text-sm"
                    disabled={loading}
                  >
                    {loading ? "Saving..." : formData.id ? "Save Changes" : "Add Certification"}
                  </button>
                </div>
              </form>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
};

export default CertificationsSection;
